/**
 * Analyzing — iter 85 (Feb 28 2026)
 *
 * Founder brief for Phase 3: "Transform this from a loading screen
 * into a confidence-building experience. While processing: explain
 * what AI is analyzing, reassure users that recommendations are
 * personalized, reinforce privacy, make the wait feel intentional.
 * The goal is anticipation, not waiting."
 *
 * Changes from the previous iter:
 *   • 4-phase cycle now has BOTH a label AND a short "what we're
 *     looking at" subline so the user understands what's happening
 *     instead of just reading poetic filler.
 *   • Persistent privacy strip ("Encrypted. Never sold. Never shared.")
 *     anchored below the phase rail — a real reassurance, not just a
 *     vague promise hidden in marketing copy.
 *   • All strings moved to i18n under `analyzing.*` (was hardcoded EN).
 *     Per the founder's Phase-3 brief we only ship EN this iter; FR/PT/ES
 *     fall through to EN until the next locale pass.
 *   • Mobile: tighter padding (`px-5 sm:px-6`), smaller heading on
 *     phones (`text-2xl sm:text-3xl`), max-w narrowed so the privacy
 *     line stays close to the action.
 */
import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, Lock } from "lucide-react";
import { useSession } from "../context/SessionContext";
import { useStealth } from "../context/StealthContext";
import { analyzeDemoSelfie, analyzeSelfie } from "../lib/api";
import { stealthSaveRoutine } from "../lib/stealthDb";
import { toast } from "sonner";
import UpgradeModal from "../components/UpgradeModal";
import { track } from "../lib/analytics";
import { trackFP } from "../lib/firstPartyAnalytics";
import { SHADE_MATCHING_UI_ENABLED } from "../lib/featureFlags";
import DemoPrivacyNotice from "../components/DemoPrivacyNotice";

// Phase definitions live next to the component because they're tightly
// coupled to the visual rhythm (4 pills @ 2.4s each). The translation
// keys are the contract; the order is fixed.
const PHASE_KEYS = SHADE_MATCHING_UI_ENABLED
  ? ["features", "tone", "shades", "steps"]
  : ["features", "tone", "steps"];

const Analyzing = ({ ephemeral = false }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { photo, preferences, setRoutine, inspiration, setInspiration } = useSession();
  const { stealth } = useStealth();
  const [phase, setPhase] = useState(0);
  const [paywall, setPaywall] = useState(null); // { used, limit } | null
  const triggered = useRef(false);

  useEffect(() => {
    if (!photo) {
      navigate(ephemeral ? "/demo/capture" : "/capture");
      return;
    }
    const id = setInterval(() => {
      setPhase((p) => (p + 1) % PHASE_KEYS.length);
    }, 2400);
    return () => clearInterval(id);
  }, [photo, navigate, ephemeral]);

  useEffect(() => {
    if (!photo || triggered.current) return;
    triggered.current = true;
    track("analyze_started", { goal: preferences.goal, stealth });

    (async () => {
      try {
        const payload = {
          photo_base64: photo.base64,
          mime_type: photo.mime,
          // First-time users no longer see chips pre-selected in
          // /onboarding (defaults are null), so `goal` / `experience`
          // can be null if a user skipped onboarding and went
          // straight to /capture. Omit those fields when null so the
          // backend's Pydantic defaults take over instead of sending
          // an invalid `null` payload that would 422 the request.
          goal: preferences.goal || undefined,
          experience: preferences.experience || undefined,
          // `?? undefined` (not `||`) so an explicit `false` is preserved.
          beard_shadow: preferences.beard_shadow ?? undefined,
          notes: preferences.notes || undefined,
          lip_finish: preferences.lip_finish || undefined,
          eye_finish: preferences.eye_finish || undefined,
          coverage: preferences.coverage || undefined,
          texture: preferences.texture || undefined,
          skip_categories: preferences.skip_categories || [],
          ethics: preferences.ethics || [],
          owned_products:
            (preferences.owned_products || "").trim() || undefined,
          contour_choice: preferences.contour_choice ?? undefined,
          inspiration: inspiration || undefined,
          stealth: ephemeral ? true : stealth,
          ...(ephemeral
            ? {
                allergens: preferences.allergens || [],
                skin_concerns: preferences.skin_concerns || [],
                life_stage: preferences.life_stage || undefined,
                locale: (i18n.language || "en").slice(0, 2),
              }
            : {}),
        };
        const demoResponse = ephemeral
          ? await analyzeDemoSelfie(payload)
          : null;
        if (ephemeral && demoResponse?.accepted === false) {
          const copy = demoResponse?.trouve_message;
          toast.error(copy?.intro || t("analyzing.errorFallback"), {
            description: copy?.caveat,
          });
          navigate("/demo/capture");
          return;
        }
        const result = ephemeral ? demoResponse?.routine : await analyzeSelfie(payload);
        if (!result) throw new Error("Demo analysis returned no routine");
        if (stealth && !ephemeral) {
          // Server didn't persist; save locally so My Routines can find it.
          await stealthSaveRoutine(result);
        }
        setRoutine(result);
        if (inspiration) {
          setInspiration(null);
          track("inspiration_routine_generated", {
            steps: result.steps?.length || 0,
          });
        }
        track("analyze_succeeded", { steps: result.steps?.length || 0 });
        trackFP("routine_generated", { steps: result.steps?.length || 0 });
        navigate(ephemeral ? "/demo/routine" : `/routine/${result.id}`);
      } catch (e) {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.debug("[analyzing] /analyze failed:", e?.response?.status || e?.message);
        }
        if (e?.response?.status === 402) {
          const detail = e.response.data?.detail || {};
          track("paywall_shown", { used: detail.used, limit: detail.limit });
          setPaywall({
            used: detail.used ?? 3,
            limit: detail.limit ?? 3,
          });
          return;
        }
        track("analyze_failed", { status: e?.response?.status });
        const msg =
          e?.response?.data?.detail ||
          t("analyzing.errorFallback");
        toast.error(msg);
        navigate(ephemeral ? "/demo/capture" : "/capture");
      }
    })();
  }, [photo, preferences, setRoutine, navigate, stealth, inspiration, setInspiration, t, i18n.language, ephemeral]);

  const phaseKey = PHASE_KEYS[phase];

  return (
    <div className="min-h-screen trubeaute-gradient flex flex-col items-center justify-center px-5 sm:px-6 py-10">
      {ephemeral && (
        <div className="w-full mb-8">
          <DemoPrivacyNotice compact />
        </div>
      )}
      <div className="max-w-md w-full text-center" data-testid="analyzing-root">
        {/* Halo + sparkle — rotating ring + soft pulse to indicate
            ongoing work without feeling busy. */}
        <motion.div
          className="relative mx-auto w-24 h-24 sm:w-28 sm:h-28 mb-8"
          animate={{ rotate: 360 }}
          transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
        >
          <div className="absolute inset-0 rounded-full border border-[#EADDD7]" />
          <div className="absolute inset-0 rounded-full border-t-2 border-[#9B6A6C]" />
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div
              animate={{ scale: [1, 1.08, 1] }}
              transition={{ duration: 2.2, repeat: Infinity }}
              className="h-12 w-12 sm:h-14 sm:w-14 rounded-full bg-gradient-to-br from-[#D5A8A4] to-[#9B6A6C] flex items-center justify-center soft-shadow"
            >
              <Sparkles className="h-6 w-6 text-white" />
            </motion.div>
          </div>
        </motion.div>

        <h2
          className="font-serif italic text-2xl sm:text-3xl text-[#2C2123] mb-2"
          data-testid="analyzing-headline"
        >
          {t("analyzing.headline")}
        </h2>
        <p className="text-sm text-[#8A7D82] mb-7">
          {t("analyzing.subline")}
        </p>

        {/* Phase label + subline — both change in sync. The label is
            what we're doing right now; the subline tells the user WHY
            it matters / what makes the routine personal. */}
        <motion.div
          key={phaseKey}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          className="min-h-[64px] flex flex-col items-center justify-center"
        >
          <p
            className="text-[15px] font-medium text-[#2C2123]"
            data-testid="analyzing-phase-label"
          >
            {t(`analyzing.phases.${phaseKey}.label`)}
          </p>
          <p
            className="text-[13px] text-[#5C4B51] mt-1"
            data-testid="analyzing-phase-sub"
          >
            {t(`analyzing.phases.${phaseKey}.sub`)}
          </p>
        </motion.div>

        {/* Phase rail — 4 pills, the active one fills, the others stay
            quiet. Subtle but tells the user this is finite. */}
        <div className="flex items-center justify-center gap-1.5 mt-5">
          {PHASE_KEYS.map((k, i) => (
            <div
              key={k}
              className={`h-1.5 w-7 rounded-full transition-colors duration-500 ${
                i === phase ? "bg-[#9B6A6C]" : "bg-[#EADDD7]"
              }`}
            />
          ))}
        </div>

        {/* Persistent privacy strip — anchored below the phase rail so
            the reassurance is RIGHT THERE the moment the user looks
            up from the spinner. Lock icon + tight one-liner. */}
        <div
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-white/70 border border-[#EADDD7] px-3.5 py-1.5"
          data-testid="analyzing-privacy"
        >
          <Lock className="h-3.5 w-3.5 text-[#9B6A6C]" aria-hidden="true" />
          <span className="text-[12px] text-[#5C4B51]">
            {ephemeral
              ? t("demo.ephemeral.processing")
              : t("analyzing.privacy")}
          </span>
        </div>
      </div>

      <UpgradeModal
        open={!!paywall}
        used={paywall?.used ?? 3}
        limit={paywall?.limit ?? 3}
        onClose={() => {
          setPaywall(null);
          navigate(ephemeral ? "/demo/capture" : "/capture");
        }}
      />
    </div>
  );
};

export default Analyzing;
